// MinutesPurchase.js
import React, { useState } from 'react';
import { Info, Loader2, AlertCircle } from 'lucide-react';
import styles from './MinutesPurchase.module.css';
import { authenticatedFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// eslint-disable-next-line
const MINUTE_RATE = {
    standard: 0.0333   // $1 = 30 standard minutes
};

const MAX_PURCHASABLE_MINUTES = 600;

const MinutesPurchase = ({ currentPlan }) => {
    const { updateUserPlan } = useAuth();
    const [standardMinutes, setStandardMinutes] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);

    const currentPurchasedMinutes = currentPlan?.limits?.non_live_minutes?.purchased?.minutes || 0;
    const purchasedMinutesExpiry = currentPlan?.limits?.non_live_minutes?.purchased?.expiry;

    // Calculations
    const totalCost = (standardMinutes / 30).toFixed(2);

    const formatExpiryDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleMinutesChange = (value) => {
        const numValue = Number(value);

        if (numValue < 0) return;
        if (numValue + currentPurchasedMinutes > MAX_PURCHASABLE_MINUTES) {
            setError(`Cannot have more than ${MAX_PURCHASABLE_MINUTES} total minutes (including current purchased minutes)`);
            return;
        }

        setStandardMinutes(numValue);
        setError(null);
    };

    const renderCurrentPurchasedMinutes = () => {
        if (!currentPurchasedMinutes) return null;

        return (
            <div className={styles.currentPurchasedSection}>
                <div className={styles.purchasedHeader}>
                    <h4>Current Purchased Minutes</h4>
                    <strong>{currentPurchasedMinutes}</strong>
                </div>
                <div className={styles.purchasedDetails}>
                    {purchasedMinutesExpiry && (
                        <div className={styles.expiryNote}>
                            Expires on: {formatExpiryDate(purchasedMinutesExpiry)}
                            <div className={styles.tooltipContainer}>
                                <Info size={16} className={styles.infoIcon} />
                                <div className={styles.tooltip}>
                                    Buying more before expiry adds remaining minutes to the next bundle and extends their expiry.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handlePurchase = async () => {
        // Validation
        if (standardMinutes === 0) {
            setError('Please select minutes to purchase');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await authenticatedFetch('/api/plans/buy-minutes', {
                method: 'POST',
                body: {
                    minute_type: 'non_live',
                    usd_amount: totalCost
                }
            });

            if (response.status === 'error') {
                throw new Error(response.error || 'Failed to purchase minutes');
            }

            await updateUserPlan();
            setPurchaseSuccess(true);
        } catch (err) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (purchaseSuccess) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.successContainer}>
                    <div className={styles.successIcon}>✓</div>
                    <h3>Purchase Successful!</h3>
                    <p>Your additional minutes have been added to your account.</p>
                    <button
                        className={styles.continueButton}
                        onClick={() => setPurchaseSuccess(false)}
                    >
                        Purchase More Minutes
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {error && (
                <div className={styles.errorMessage}>
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}
            <div className={styles.minutesContainer}>
                <div className={styles.purchaseContainer}>
                    <div className={styles.minutesSection}>
                        <div className={styles.minuteTypeHeader}>
                            <h3>Recording Minutes</h3>
                            <div className={styles.tooltipContainer}>
                                <Info size={16} className={styles.infoIcon} />
                                <div className={styles.tooltip}>
                                    Minutes for recording sessions
                                </div>
                            </div>
                        </div>
                        <div className={styles.inputContainer}>
                            <input
                                type="number"
                                value={standardMinutes}
                                onChange={(e) => handleMinutesChange(e.target.value)}
                                min="0"
                                max={MAX_PURCHASABLE_MINUTES - currentPurchasedMinutes}
                                className={styles.minuteInput}
                            />
                            <span className={styles.inputLabel}>minutes</span>
                        </div>
                        <div className={styles.costDisplay}>
                            Cost: ${totalCost} (1 USD = 30 minutes)
                        </div>
                    </div>
                </div>

                <div className={styles.summarySection}>
                    <h4>Purchase Summary</h4>
                    <div className={styles.summaryDetails}>
                        <div> Recording Minutes: {standardMinutes}</div>
                        <div className={styles.totalCost}>Total Cost: ${totalCost}</div>
                    </div>
                </div>

            </div>

            <div className={styles.actionSection}>
                <button
                    className={`${styles.purchaseButton} ${loading ? styles.loading : ''}`}
                    onClick={handlePurchase}
                    disabled={loading || standardMinutes === 0}
                >
                    {loading ? (
                        <>
                            <Loader2 className={styles.spinnerIcon} size={16} />
                            Processing Purchase...
                        </>
                    ) : (
                        `Purchase ${totalCost > 0 ? `$${totalCost}` : ''}`
                    )}
                </button>
            </div>
            {renderCurrentPurchasedMinutes()}
        </div>
    );
};

export default MinutesPurchase;